import React, { useState } from "react";
import {
  Modal,
  Button,
  Card,
  Table,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconDownload,
} from "@tabler/icons-react";
import ATVForm from "./ATVForm";
import { useATV } from "../context/ATVContext";
import { useATVStatus } from "../context/ATVStatusContext";
import { getStatusConfig } from "../utils/statusUtils";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ATVCRUD = ({ show, onHide }) => {
  const { atvs, filterOptions, handleSearch, refetch, deleteATV, Pagination } =
    useATV();
  const { atvStatuses } = useATVStatus();

  const [showForm, setShowForm] = useState(false);
  const [editingData, setEditingData] = useState(null);

  const handleCreate = () => {
    setEditingData(null);
    setShowForm(true);
  };

  const handleEdit = (atv) => {
    setEditingData(atv);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this ATV?")) {
      return;
    }

    try {
      await deleteATV(id);
      refetch(); // Refresh the data
    } catch (error) {
      console.error("Error deleting ATV:", error);
      alert("Failed to delete ATV. Please try again.");
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    refetch(); // Refresh the data
  };

  const handleFormError = (error) => {
    console.error("Form error:", error);
  };

  const handleExport = () => {
    const exportData = atvs.data.map((atv) => ({
      "Plate Number": atv.plate_number,
      Name: atv.name,
      Model: atv.model,
      Year: atv.year,
      Status: atv.status?.name || "Unknown",
      "Last Service": formatDate(atv.last_service),
      "Next Service": formatDate(atv.next_service),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ATVs");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, "atv_data.xlsx");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>ATV Management</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            minHeight: "60vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
                  ATV Management
                </h2>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    variant="success"
                    onClick={handleExport}
                    disabled={atvs.loading || atvs.data.length === 0}
                  >
                    <IconDownload size={16} className="me-2" />
                    Export
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCreate}
                    disabled={atvs.loading}
                  >
                    <IconPlus size={16} className="me-2" />
                    Add ATV
                  </Button>
                </div>
              </div>{" "}
              {/* Search */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search ATVs..."
                  onKeyPress={handleSearch}
                  defaultValue={filterOptions.search}
                />
              </div>
              {atvs.error && (
                <Alert variant="danger" className="mb-3">
                  {atvs.error}
                </Alert>
              )}
              <Card>
                <div style={{ padding: "0" }}>
                  {atvs.loading ? (
                    <div className="text-center p-4">
                      <Spinner animation="border" />
                      <p className="mt-2">Loading ATVs...</p>
                    </div>
                  ) : (
                    <Table striped bordered hover style={{ marginBottom: "0" }}>
                      <thead>
                        <tr>
                          <th>Plate Number</th>
                          <th>Name</th>
                          <th>Model</th>
                          <th>Year</th>
                          <th>Status</th>
                          <th>Last Service</th>
                          <th>Next Service</th>
                          <th style={{ textAlign: "center", width: "150px" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {atvs.data.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              No ATVs found. Click "Add ATV" to create your
                              first ATV.
                            </td>
                          </tr>
                        ) : (
                          atvs.data.map((atv, index) => {
                            const statusConfig = getStatusConfig(
                              atv.status_id,
                              atvStatuses.data
                            );
                            return (
                              <tr key={atv.id || index}>
                                <td>{atv.plate_number}</td>
                                <td>{atv.name}</td>
                                <td>{atv.model}</td>
                                <td>{atv.year}</td>
                                <td>
                                  <Badge bg={statusConfig.variant} text="white">
                                    {atv.status?.name || "Unknown"}
                                  </Badge>
                                </td>
                                <td>{formatDate(atv.last_service)}</td>
                                <td>{formatDate(atv.next_service)}</td>
                                <td style={{ textAlign: "center" }}>
                                  <Button
                                    variant="outline-primary"
                                    className="m-1"
                                    onClick={() => handleEdit(atv)}
                                  >
                                    <IconEdit size={14} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    onClick={() => handleDelete(atv.id)}
                                    className="m-1"
                                  >
                                    <IconTrash size={14} />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Card>
              {/* Pagination */}
              {atvs.data.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  {Pagination}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create/Edit Modal */}
        <Modal show={showForm} onHide={() => setShowForm(false)} size="lg" className="workshop-modal-scope">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingData ? "Edit ATV" : "Add New ATV"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ATVForm
              initialData={editingData}
              onSuccess={handleFormSuccess}
              onError={handleFormError}
            />
          </Modal.Body>
        </Modal>
      </Modal.Body>
    </Modal>
  );
};

export default ATVCRUD;
